import { Form, FormikProvider, useFormik } from "formik";
import {
    useCallback,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import { CloseTemplate } from "@common/types/closeTemplate";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import { TaskType } from "@common/types/task";
import { pluralizeWithCount } from "@common/helpers/string";
import { useWorkspacesList } from "@api/workspace.api";
import { useApplyCloseTemplateMutation } from "@api/closeTemplate.api";
import { useCloseTemplateTaskTemplates } from "@api/taskTemplate.api";
import { useServiceTemplateSchedules } from "@api/serviceTemplateSchedule.api";
import {
    useCurrentOrganizationMembers,
    usePendingOrganizationInvites,
} from "@api/organizationMember.api";
import { validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { ApplyCloseTemplateStepChooseType } from "./ApplyCloseTemplateStepChooseType";
import { ApplyCloseTemplateStepDefaults } from "./ApplyCloseTemplateStepDefaults";
import { ApplyCloseTemplateStepEntitySelection } from "./ApplyCloseTemplateStepEntitySelection";
import {
    ApplyCloseTemplateStepReview,
    SelectedEntityEntry,
} from "./ApplyCloseTemplateStepReview";
import { ScheduleWizardStepDefaults } from "./ScheduleWizardStepDefaults";
import { ScheduleWizardStepTeam } from "./ScheduleWizardStepTeam";
import { useScheduleWizardPreviewOccurrences } from "./useScheduleWizardPreviewOccurrences";
import { useCreateSchedulesForEntities } from "./useCreateSchedulesForEntities";
import { TemplateApplicationProvider } from "./TemplateApplicationProvider";
import {
    createApplyCloseTemplateStep1FormSchema,
    createApplyCloseTemplateStep2ValidationSchema,
    createApplyCloseTemplateInitialValues,
    type ApplyCloseTemplateWizardValues,
} from "./applyCloseTemplate.schema";
import {
    createScheduleStep1FormSchema,
    createScheduleStep2ValidationSchema,
    createScheduleWizardInitialValues,
} from "./scheduleWizard.schema";
import { filterOrganizationMembersWithAnyWorkspaceAccess } from "./applyCloseTemplateOrganizationMembers";
import { useApplyCloseTemplateWorkspaceMembers } from "./useApplyCloseTemplateWorkspaceMembers";

export type TemplateWizardVariant = "apply" | "schedule";

const APPLY_WIZARD_TOTAL_STEPS = 4;
const SCHEDULE_WIZARD_TOTAL_STEPS = 5;

interface Props {
    closeTemplate?: CloseTemplate;
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
    // Chosen on step 1 of the merged wizard (see PRO-10556) rather than
    // passed in as a prop — the user now picks one-off vs. recurring inside
    // a single "Apply" flow instead of the drawer being opened in a fixed
    // variant from two separate entry points. The state lives in the parent
    // drawer so it survives the body remount when the workspaces query
    // resolves and changes the body's key.
    variant: TemplateWizardVariant | null;
    setVariant: Dispatch<SetStateAction<TemplateWizardVariant | null>>;
    onClose: () => void;
    /** Called instead of onClose when the wizard has completed successfully. */
    onApplyComplete?: () => void;
}

type ApplyRowWithRequiredFields =
    ApplyCloseTemplateWizardValues["entityRows"][string] & {
        startDate: string;
        endDate: string;
        workStartDate: string;
        workDeliveryDate: string;
    };

export const ApplyCloseTemplateWizardBody = ({
    closeTemplate,
    step,
    setStep,
    variant,
    setVariant,
    onClose,
    onApplyComplete,
}: Props) => {
    const isScheduleVariant = variant === "schedule";
    const totalSteps = isScheduleVariant
        ? SCHEDULE_WIZARD_TOTAL_STEPS
        : APPLY_WIZARD_TOTAL_STEPS;
    const { organizationId } = useScope();
    const { toast: successToast, error: errorToast } = useToaster();
    const { data: workspaces } = useWorkspacesList({
        organizationId: organizationId ?? undefined,
        includeEntities: true,
    });
    const { data: members } = useCurrentOrganizationMembers();
    const { data: pendingInvites, isFetching: isPendingInvitesFetching } =
        usePendingOrganizationInvites();
    const applyMutation = useApplyCloseTemplateMutation(closeTemplate?.id);
    const { data: existingSchedules } = useServiceTemplateSchedules({
        organizationId,
        templateId: closeTemplate?.id,
        enabled: isScheduleVariant && closeTemplate?.id != null,
    });
    const scheduledEntityUuids = useMemo(
        () => new Set((existingSchedules ?? []).map((s) => s.entityUuid)),
        [existingSchedules],
    );

    const [appliedEntityIds, setAppliedEntityIds] = useState(
        () => new Set<number>(),
    );
    const [applyingEntityId, setApplyingEntityId] = useState<number | null>(
        null,
    );
    const [isApplyingBulk, setIsApplyingBulk] = useState(false);

    /**
     * Emails that have a pending (unused) org invite code — these users have
     * been invited but haven't accepted yet and should not be surfaced as
     * assignable team members while applying a template.
     */
    const pendingInviteEmailSet = useMemo(
        () =>
            new Set(
                (pendingInvites ?? [])
                    .map((i) => i.email?.toLowerCase())
                    .filter((email): email is string => email != null),
            ),
        [pendingInvites],
    );

    const memberItems = useMemo(() => {
        // Wait for pending invites to load before surfacing members. While the
        // invites request is in flight the email set is empty, so every member
        // would pass the filter and pending-invite users could briefly be
        // selected as assignees.
        if (isPendingInvitesFetching) {
            return [];
        }
        return (members ?? []).filter(
            (m) =>
                !m.user.email ||
                !pendingInviteEmailSet.has(m.user.email.toLowerCase()),
        );
    }, [members, pendingInviteEmailSet, isPendingInvitesFetching]);

    const { data: templateTasks = [] } = useCloseTemplateTaskTemplates(
        closeTemplate?.id ?? null,
    );
    const activeRoles = useMemo(
        () =>
            new Set(
                templateTasks
                    .map((t) => t.assigneeRole)
                    .filter((r): r is TaskTemplateAssigneeRole => r !== null),
            ),
        [templateTasks],
    );

    const templateTaskTypes = useMemo(
        () => [
            ...new Set(templateTasks.map((t) => t.type ?? TaskType.QUESTION)),
        ],
        [templateTasks],
    );

    // When the template is scoped to a specific workspace, restrict the
    // entity selection to that workspace only so the user cannot apply
    // it to entities outside the intended workspace.
    const templateWorkspaceId = closeTemplate?.workspaceId;
    const workspaceScopedWorkspaces = useMemo(
        () =>
            templateWorkspaceId != null
                ? (workspaces?.filter((ws) => ws.id === templateWorkspaceId) ??
                  [])
                : workspaces,
        [templateWorkspaceId, workspaces],
    );

    const isClientSpecificTemplate = templateWorkspaceId != null;

    const {
        organizationWorkspaceIds,
        workspaceMembersMap,
        defaultClientIdByWorkspaceId,
    } = useApplyCloseTemplateWorkspaceMembers({
        organizationId: organizationId ?? null,
        workspaces,
    });

    const membersWithOrgWorkspaceAccess = useMemo(
        () =>
            filterOrganizationMembersWithAnyWorkspaceAccess(
                memberItems,
                organizationWorkspaceIds,
            ),
        [memberItems, organizationWorkspaceIds],
    );

    const initialValues = useMemo(
        () =>
            isScheduleVariant
                ? createScheduleWizardInitialValues(workspaces)
                : createApplyCloseTemplateInitialValues(
                      workspaces,
                      defaultClientIdByWorkspaceId,
                  ),
        [defaultClientIdByWorkspaceId, isScheduleVariant, workspaces],
    );

    const formik = useFormik<ApplyCloseTemplateWizardValues>({
        initialValues,
        enableReinitialize: true,
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {},
    });

    const getMemberLabel = useCallback(
        (userId: number | null) => {
            if (userId == null) {
                return "";
            }
            const member = memberItems.find((m) => m.user.id === userId);
            if (!member) {
                return String(userId);
            }
            const fullName =
                `${member.user.firstName} ${member.user.lastName}`.trim();
            if (fullName) {
                return fullName;
            }
            return member.user.email ?? String(userId);
        },
        [memberItems],
    );

    const validateStep1 = useMemo(
        () =>
            validateFormikWithZodSchema(
                createApplyCloseTemplateStep1FormSchema(
                    membersWithOrgWorkspaceAccess,
                ),
            ),
        [membersWithOrgWorkspaceAccess],
    );
    const validateStep2 = useMemo(
        () =>
            validateFormikWithZodSchema(
                createApplyCloseTemplateStep2ValidationSchema(
                    memberItems,
                    activeRoles,
                ),
            ),
        [memberItems, activeRoles],
    );
    const validateScheduleEntitySelection = useMemo(
        () =>
            validateFormikWithZodSchema(
                createScheduleStep2ValidationSchema(memberItems, activeRoles),
            ),
        [memberItems, activeRoles],
    );

    const handleContinueFromChooseType = useCallback(() => {
        if (variant === null) {
            return;
        }
        setStep(2);
    }, [setStep, variant]);

    const handleContinueFromApplyStep1 = useCallback(() => {
        const parsed = createApplyCloseTemplateStep1FormSchema(
            membersWithOrgWorkspaceAccess,
        ).safeParse({
            defaults: formik.values.defaults,
        });
        if (!parsed.success) {
            const firstMessage =
                parsed.error.issues[0]?.message ?? "Invalid dates";
            errorToast(firstMessage);
            const errors = validateStep1({
                defaults: formik.values.defaults,
            });
            formik.setErrors(errors);
            formik.setTouched({
                defaults: {
                    startDate: true,
                    endDate: true,
                    workStartDate: true,
                    workDeliveryDate: true,
                },
            });
            return;
        }
        formik.setErrors({});
        setStep(3);
    }, [
        errorToast,
        formik,
        membersWithOrgWorkspaceAccess,
        setStep,
        validateStep1,
    ]);

    const handleContinueFromScheduleOccurrences = useCallback(() => {
        const parsed = createScheduleStep1FormSchema().safeParse({
            defaults: formik.values.defaults,
        });
        if (!parsed.success) {
            errorToast(
                parsed.error.issues[0]?.message ?? "Invalid schedule settings",
            );
            return;
        }
        setStep(3);
    }, [errorToast, formik.values.defaults, setStep]);

    const handleContinueFromScheduleTeam = useCallback(() => {
        setStep(4);
    }, [setStep]);

    const handleContinueFromEntitySelection = useCallback(() => {
        if (isScheduleVariant) {
            const parsed = createScheduleStep2ValidationSchema(
                memberItems,
                activeRoles,
            ).safeParse({
                entityRows: formik.values.entityRows,
            });
            if (!parsed.success) {
                const firstMessage =
                    parsed.error.issues[0]?.message ?? "Invalid selection";
                errorToast(firstMessage);
                const errors = validateScheduleEntitySelection({
                    entityRows: formik.values.entityRows,
                });
                formik.setErrors(errors);
                return;
            }
            formik.setErrors({});
            setStep(5);
            return;
        }

        const parsed = createApplyCloseTemplateStep2ValidationSchema(
            memberItems,
            activeRoles,
        ).safeParse({
            entityRows: formik.values.entityRows,
        });
        if (!parsed.success) {
            const firstMessage =
                parsed.error.issues[0]?.message ?? "Invalid selection";
            errorToast(firstMessage);
            const errors = validateStep2({
                entityRows: formik.values.entityRows,
            });
            formik.setErrors(errors);
            return;
        }
        formik.setErrors({});
        setStep(4);
    }, [
        activeRoles,
        errorToast,
        formik,
        isScheduleVariant,
        memberItems,
        setStep,
        validateScheduleEntitySelection,
        validateStep2,
    ]);

    const entitySelectionStep = isScheduleVariant ? 4 : 3;
    const reviewStep = isScheduleVariant ? 5 : 4;

    // First upcoming occurrence the schedule would create, used to preview
    // the review step's per-entity task list against realistic dates even
    // though the schedule itself hasn't been created yet (see PRO-10537).
    const schedulePreviewOccurrences = useScheduleWizardPreviewOccurrences(
        formik.values.defaults.schedule,
    );
    const scheduleFirstOccurrence = schedulePreviewOccurrences[0];

    const reviewEntries = useMemo((): SelectedEntityEntry[] => {
        const out: SelectedEntityEntry[] = [];
        Object.entries(formik.values.entityRows).forEach(
            ([entityIdStr, row]) => {
                if (!row.isSelected) {
                    return;
                }
                // Defense-in-depth against duplicate schedules: never carry an
                // already-scheduled entity into the review/submit path. The
                // entity-selection step disables these rows, but a row can be
                // selected before the schedules query resolves, after which the
                // row is only disabled (its `isSelected` value is never
                // cleared). Guarding here keeps both the step-4 preview and
                // `createSchedules` from acting on a stale selection.
                if (
                    isScheduleVariant &&
                    row.entityUuid != null &&
                    scheduledEntityUuids.has(row.entityUuid)
                ) {
                    return;
                }
                // Dates come from the first previewed occurrence; assignees
                // stay on the per-entity row (copied from team defaults on
                // select, then editable in the role columns).
                const scheduleOverrides =
                    isScheduleVariant && scheduleFirstOccurrence
                        ? {
                              startDate:
                                  scheduleFirstOccurrence.serviceStartDate,
                              endDate: scheduleFirstOccurrence.serviceEndDate,
                              workStartDate:
                                  scheduleFirstOccurrence.workStartDate,
                              workDeliveryDate:
                                  scheduleFirstOccurrence.workEndDate,
                          }
                        : null;
                out.push({
                    entityIdStr,
                    row: scheduleOverrides
                        ? { ...row, ...scheduleOverrides }
                        : row,
                });
            },
        );
        out.sort((a, b) => {
            const byClient = a.row.clientLabel.localeCompare(b.row.clientLabel);
            if (byClient !== 0) {
                return byClient;
            }
            return a.row.entityName.localeCompare(b.row.entityName);
        });
        return out;
    }, [
        formik.values.entityRows,
        isScheduleVariant,
        scheduleFirstOccurrence,
        scheduledEntityUuids,
    ]);

    const hasRequiredApplyFields = useCallback(
        (
            row: ApplyCloseTemplateWizardValues["entityRows"][string],
        ): row is ApplyRowWithRequiredFields =>
            row.startDate != null &&
            row.endDate != null &&
            row.workStartDate != null &&
            row.workDeliveryDate != null,
        [],
    );

    const handleApplyAll = useCallback(async () => {
        if (!closeTemplate || isApplyingBulk) {
            return;
        }

        setIsApplyingBulk(true);
        const applyToEntities = async () => {
            let closesCreated = 0;
            let tasksCreated = 0;
            let succeededEntities = 0;
            let failedEntities = 0;
            let firstFailureMessage: string | null = null;
            const pending = reviewEntries.filter(
                (e) => !appliedEntityIds.has(Number(e.entityIdStr)),
            );
            for (const { entityIdStr, row } of pending) {
                if (!hasRequiredApplyFields(row)) {
                    failedEntities += 1;
                    firstFailureMessage =
                        firstFailureMessage ??
                        "Service start/end and work start/end dates are required";
                    continue;
                }
                try {
                    const entityId = Number(entityIdStr);
                    setApplyingEntityId(entityId);
                    const result = await applyMutation.mutateAsync({
                        workspaceId: row.workspaceId,
                        entityIds: [entityId],
                        startDate: row.startDate,
                        endDate: row.endDate,
                        preparerId: row.preparerId,
                        reviewerId: row.reviewerId,
                        managerId: row.managerId ?? null,
                        clientId: row.clientId ?? null,
                        workStartDate: row.workStartDate,
                        workDeliveryDate: row.workDeliveryDate,
                    });
                    closesCreated += result.closesCreated;
                    tasksCreated += result.tasksCreated;
                    succeededEntities += 1;
                    setAppliedEntityIds((prev) =>
                        new Set(prev).add(Number(entityIdStr)),
                    );
                } catch (err) {
                    failedEntities += 1;
                    const message =
                        err instanceof Error
                            ? err.message
                            : "Failed to apply template";
                    firstFailureMessage = firstFailureMessage ?? message;
                }
            }
            if (succeededEntities > 0) {
                successToast(
                    `Applied "${closeTemplate?.name ?? ""}": ${pluralizeWithCount("work period", closesCreated)} and ${pluralizeWithCount("task", tasksCreated)} created`,
                );
            }
            if (failedEntities > 0) {
                const failedBaseMessage = `${pluralizeWithCount("entity", failedEntities)} failed to apply`;
                errorToast(
                    firstFailureMessage
                        ? `${failedBaseMessage}: ${firstFailureMessage}`
                        : failedBaseMessage,
                );
            }
            if (failedEntities === 0) {
                (onApplyComplete ?? onClose)();
            }
        };
        await applyToEntities().finally(() => {
            setIsApplyingBulk(false);
            setApplyingEntityId(null);
        });
    }, [
        appliedEntityIds,
        applyMutation,
        closeTemplate,
        errorToast,
        hasRequiredApplyFields,
        isApplyingBulk,
        onApplyComplete,
        onClose,
        reviewEntries,
        successToast,
    ]);

    const handleAllSchedulesCreated = useCallback(() => {
        (onApplyComplete ?? onClose)();
    }, [onApplyComplete, onClose]);

    const {
        createSchedules,
        createdEntityIds,
        creatingEntityId,
        isCreatingBulk,
    } = useCreateSchedulesForEntities({
        organizationId,
        closeTemplate,
        schedule: formik.values.defaults.schedule,
        onAllCreated: handleAllSchedulesCreated,
    });

    const handleBack = useCallback(() => {
        formik.setErrors({});
        setStep((s) => s - 1);
    }, [formik, setStep]);

    return (
        <FormikProvider value={formik}>
            <TemplateApplicationProvider
                members={memberItems}
                getMemberLabel={getMemberLabel}
                workspaceMembersMap={workspaceMembersMap}
            >
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                >
                    {step === 1 ? (
                        <ApplyCloseTemplateStepChooseType
                            variant={variant}
                            onSelectVariant={setVariant}
                            onContinue={handleContinueFromChooseType}
                            totalSteps={totalSteps}
                        />
                    ) : null}

                    {step === 2 && isScheduleVariant ? (
                        <ScheduleWizardStepDefaults
                            onBack={handleBack}
                            onContinue={handleContinueFromScheduleOccurrences}
                        />
                    ) : null}

                    {step === 2 && !isScheduleVariant ? (
                        <ApplyCloseTemplateStepDefaults
                            onBack={handleBack}
                            onContinue={handleContinueFromApplyStep1}
                            memberOptions={membersWithOrgWorkspaceAccess}
                            activeRoles={activeRoles}
                        />
                    ) : null}

                    {step === 3 && isScheduleVariant ? (
                        <ScheduleWizardStepTeam
                            onBack={handleBack}
                            onContinue={handleContinueFromScheduleTeam}
                        />
                    ) : null}

                    {step === entitySelectionStep ? (
                        <ApplyCloseTemplateStepEntitySelection
                            workspaces={workspaceScopedWorkspaces}
                            activeRoles={activeRoles}
                            templateTaskTypes={templateTaskTypes}
                            onBack={handleBack}
                            onContinue={handleContinueFromEntitySelection}
                            subtitle={
                                isClientSpecificTemplate
                                    ? `Step ${entitySelectionStep} of ${totalSteps} · Select entities`
                                    : `Step ${entitySelectionStep} of ${totalSteps} · Select clients`
                            }
                            searchPlaceholder={
                                isClientSpecificTemplate
                                    ? "Search entities..."
                                    : "Search clients..."
                            }
                            showPlansFilter={!isClientSpecificTemplate}
                            shouldHideDateColumns={isScheduleVariant}
                            scheduledEntityUuids={
                                isScheduleVariant
                                    ? scheduledEntityUuids
                                    : undefined
                            }
                        />
                    ) : null}

                    {step === reviewStep ? (
                        <ApplyCloseTemplateStepReview
                            closeTemplateId={closeTemplate?.id}
                            entries={reviewEntries}
                            appliedEntityIds={
                                isScheduleVariant
                                    ? createdEntityIds
                                    : appliedEntityIds
                            }
                            applyingEntityId={
                                isScheduleVariant
                                    ? creatingEntityId
                                    : applyingEntityId
                            }
                            isApplyingBulk={
                                isScheduleVariant
                                    ? isCreatingBulk
                                    : isApplyingBulk
                            }
                            onBack={handleBack}
                            onApplyAll={
                                isScheduleVariant
                                    ? () => void createSchedules(reviewEntries)
                                    : handleApplyAll
                            }
                            onClose={onApplyComplete ?? onClose}
                            applyButtonLabel={
                                isScheduleVariant ? "Create schedules" : "Apply"
                            }
                            subtitle={`Step ${reviewStep} of ${totalSteps} · Preview selection`}
                        />
                    ) : null}

                    {step === entitySelectionStep &&
                    typeof formik.errors.entityRows === "string" ? (
                        <p className="text-danger small mt-2">
                            {formik.errors.entityRows}
                        </p>
                    ) : null}
                </Form>
            </TemplateApplicationProvider>
        </FormikProvider>
    );
};
